<template>
  <div
    :class="[
      'ls-conversation-message',
      {
        'border-bottom': !isLast,
      },
      { 'ls-conversation-message--tertiary': message.user.is_admin },
      { 'ls-conversation-message--customer': isCustomer },
      { 'ls-conversation-message--contractor': isContractor },
    ]"
  >
    <div class="ls-conversation-message--upper">
      <!-- mobile header -->
      <div v-if="!screenSize.desktop" class="ls-conversation-message--mobile-header">
        <span test-id="name" :class="['has-text-weight-bold']">{{ renderName }}</span>
        <span class="ls-conversation-message--timestamp" test-id="timestamp">{{
          renderTimeStamp
        }}</span>
      </div>

      <!-- end mobile header -->

      <div :class="['ls-conversation-message--message-text']">
        <span
          v-if="screenSize.desktop"
          test-id="name"
          :class="[
            'has-text-weight-bold',
            'ls-conversation-message--name',
            { 'ls-conversation-message--primary': message.user.customer_id },
            { 'ls-conversation-message--gray': message.user.contractor_id },
            { 'ls-conversation-message--tertiary': message.user.is_admin },
          ]"
          >{{ renderName }}:</span
        >
        <span class="ls-conversation-message--message-text" test-id="message_text">{{
          message.text
        }}</span>
        <div v-if="message.images.length" class="ls-conversation-message--images">
          <div v-for="image in message.images" :key="image.id">
            <LsImage v-if="image.image_url" :src="image.image_url" />
          </div>
        </div>
      </div>
      <div v-if="screenSize.desktop" class="ls-conversation-message--timestamp">
        <span test-id="timestamp">{{ renderTimeStamp }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import moment from 'moment';
import _ from 'lodash';
import { LsImage } from '@/components/common';
import { mapState } from 'vuex';
import { lsPropTypeHelpers } from 'ls-vue-common';
import { themedComponentMixin } from '@/mixins';

export default {
  name: 'LeadsConversationMessage',
  components: { LsImage },
  mixins: [themedComponentMixin],
  props: {
    isLast: VueTypes.bool.def(false),
    message: VueTypes.shape({
      created_at: VueTypes.string.isRequired, // YYYY-MM-DD HH:mm:ss
      id: VueTypes.number.isRequired,
      text: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
      images: VueTypes.arrayOf(VueTypes.object),
      user: VueTypes.shape({
        customer_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        contractor_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        // should use absence of customer and contractor id instead of this flag
        // as deactivated accounts lose the is_admin flag
        user_id: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        first_name: VueTypes.string,
        last_name: VueTypes.string,
      }).isRequired.loose, // need loose here since the object has additional unused properties
    }).isRequired.loose, // need loose here since the object has additional unused properties
  },
  computed: {
    ...mapState(['screenSize']),
    renderTimeStamp() {
      const { created_at } = this.message;
      const currentTime = moment.utc(created_at).local();

      moment.updateLocale('en', {
        relativeTime: {
          future: 'in %s',
          past: '%s ago',
          s: 'seconds',
          ss: '%ss',
          m: '%dm',
          mm: '%dm',
          h: 'an hour',
          hh: '%dh',
          d: 'a day',
          dd: '%dd',
          M: '%dmo',
          MM: '%dmo',
          y: '%dy',
          yy: '%dy',
        },
      });

      return moment(currentTime).fromNow();
    },
    renderName() {
      const { user } = this.message;

      if (user.contractor_id) {
        return user.first_name;
      }

      if (user.customer_id) {
        return 'You';
      }

      return `${this.brandName} Support`;
    },
    isCustomer() {
      return _.get(this.message, 'user.customer_id', false);
    },
    isContractor() {
      return _.get(this.message, 'user.contractor_id', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.border-bottom {
  border-bottom: 2px solid $primary-light;
}

.ls-conversation-message {
  display: flex;
  flex-direction: column;
  width: 90%;
}

.ls-conversation-message--message-text {
  white-space: pre-line;
  word-break: break-word;
}

.ls-conversation-message--customer {
  margin-left: auto;
  background-color: $primary-light;
  margin-bottom: 10px;
}

.ls-conversation-message--contractor {
  background-color: $white;
  border: 1px solid $primary-light;
  margin-bottom: 10px;
}

.ls-conversation-message--upper {
  display: flex;
  padding: 20px;
}

.ls-conversation-message--timestamp {
  color: $primary;
  margin-left: auto;
}

.ls-conversation-message--images {
  margin-top: 10px;
}

.ls-conversation-message--name {
  display: block;
}

@media (max-width: $mobile-max-width) {
  .ls-conversation-message {
    width: 100%;
  }

  .ls-conversation-message--upper {
    width: 100%;
  }

  .ls-conversation-message--mobile-header {
    display: flex;
    justify-content: space-between;
  }

  .ls-conversation-message--message-text {
    padding: 6px 0;
  }

  .ls-conversation-message--upper {
    display: block;
  }
}
.themed-app {
  .border-bottom {
    border-color: $theme-color-primary-light;
  }
  .ls-conversation-message--customer {
    background-color: $theme-color-primary-light;
  }
  .ls-conversation-message--contractor {
    background-color: $theme-color-white;
    border-color: $theme-color-primary-light;
  }
  .ls-conversation-message--timestamp {
    color: $theme-color-primary;
  }
}
</style>
